/**
 * The @page rule of the view on screen.
 *
 * @page cannot be scoped to a component: a rule in any loaded stylesheet applies to
 * every print for the rest of the session (the app is a single page; the server render
 * prints many pages in one tab). So a view that needs its own paper size (the packing
 * box stickers, 7.6cm x 12.5cm) claims it while it is shown and releases it on leave:
 * one <style data-fed-print-page="id"> in <head>, replaced on each claim.
 *
 * Behind a per-browser toggle while it is proven on the real printers: off (the
 * default) adds no rule anywhere, which is exactly what the app did before (its old
 * @page rules were nested inside class selectors and never compiled to valid css).
 * The server render is told the toggle's value with each request and prints the same
 * way. Once the fixed pages are trusted the toggle goes and claims are unconditional.
 */
export const PRINT_PAGE_CSS_KEY = 'k.fed-print-page-css';
const ATTRIBUTE = 'data-fed-print-page';

function storage() {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch (e) {
    return null;
  }
}

/** whether prints from this browser (and renders started here) use the pages' own @page rules */
export function printPageCssEnabled() {
  const store = storage();
  return !!store && store.getItem(PRINT_PAGE_CSS_KEY) === 'true';
}

export function setPrintPageCssEnabled(on) {
  const store = storage();
  if (store) {
    store.setItem(PRINT_PAGE_CSS_KEY, on ? 'true' : 'false');
  }
}

function findClaim(id) {
  return document.head.querySelector(`style[${ATTRIBUTE}="${id}"]`);
}

/**
 * Apply an @page rule (its declarations, e.g. 'size: 7.6cm 12.5cm; margin: 0') for the
 * view with this id, replacing the view's earlier claim, with any print css that goes
 * with that paper (a screen-only border that would push a sticker onto a second sheet).
 * Nothing happens while the toggle is off.
 */
export function claimPrintPage(id, rule, css = '') {
  if (!printPageCssEnabled() || typeof document === 'undefined') {
    return;
  }
  let style = findClaim(id);
  if (!style) {
    style = document.createElement('style');
    style.setAttribute(ATTRIBUTE, id);
    document.head.appendChild(style);
  }
  style.textContent = css ? `@page { ${rule} }\n${css}` : `@page { ${rule} }`;
}

/** drop the view's @page rule (leaving is what stops it applying to later prints) */
export function releasePrintPage(id) {
  if (typeof document === 'undefined') {
    return;
  }
  const style = findClaim(id);
  if (style) {
    style.remove();
  }
}
